import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import type { WireSourceHealthRow, WireSourceInstance, WireSourceKind } from '@logistics/contracts';
import { sourceKindName } from '@/lib/glyphs';
import { cn } from '@/lib/utils';
import { button, monoLabel } from '../shell/look';
import { SidePanel } from '../shell/side-panel';
import { guideSteps, whatItReads } from './catalog';
import { GuideList } from './guide-list';
import { credentialRows, sourceStatus } from './credentials';
import { isDemoSource } from './demo-banner';
import { SourceGlyph } from './source-card';
import { useSourceActions } from './use-source-actions';

/** The source detail drawer (design L542–593). Credentials are masked, never revealed. */
export function SourcePanel({
  instance,
  descriptor,
  health,
  admin,
  onClose,
  onEdit,
}: {
  instance: WireSourceInstance;
  descriptor: WireSourceKind | undefined;
  health: WireSourceHealthRow | undefined;
  admin: boolean;
  onClose: () => void;
  onEdit: () => void;
}): JSX.Element {
  const status = sourceStatus(instance, health);
  const { test, toggle, remove } = useSourceActions(instance, onClose);
  const [confirming, setConfirming] = useState(false);
  const steps = guideSteps(descriptor);

  const header = (
    <>
      <SourceGlyph kind={instance.kind} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[17px] font-semibold">{instance.label}</div>
        <div className="text-mute text-[13px]">
          {sourceKindName(instance.kind)} ·{' '}
          <span style={{ color: status.color }}>{status.label}</span>
        </div>
      </div>
    </>
  );

  const footer = (
    <>
      <span className="text-mute truncate font-mono text-xs">{instance.sourceId}</span>
      {admin && (
        <button
          type="button"
          disabled={remove.isPending}
          onClick={() => (confirming ? remove.mutate() : setConfirming(true))}
          className={cn(
            'rounded-[9px] border px-3.5 py-[9px] text-[13px] font-medium transition-all duration-200',
            confirming ? 'border-bad bg-bad text-white' : 'border-line text-bad bg-transparent',
          )}
        >
          {confirming ? 'Confirm delete' : 'Delete source'}
        </button>
      )}
    </>
  );

  return (
    <SidePanel label={instance.label} header={header} footer={footer} onClose={onClose}>
      <Section title="CREDENTIALS">
        <Credentials descriptor={descriptor} instance={instance} />
        {admin && <Actions instance={instance} test={test} toggle={toggle} onEdit={onEdit} />}
      </Section>
      <Section title="WHAT THE AGENT READS">
        <p className="m-0 text-[13.5px] leading-[1.6]">{whatItReads(instance.kind)}</p>
      </Section>
      {steps.length > 0 && (
        <Section title="SETUP GUIDE">
          <GuideList steps={steps} />
        </Section>
      )}
    </SidePanel>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <div className={cn(monoLabel, 'mb-2.5 text-[11px]')}>{title}</div>
      {children}
    </div>
  );
}

/** Settings in plain text; secrets only as "set" — the panel is never sent one. */
function Credentials({
  descriptor,
  instance,
}: {
  descriptor: WireSourceKind | undefined;
  instance: WireSourceInstance;
}): JSX.Element {
  return (
    <>
      <div className="border-line overflow-hidden rounded-xl border">
        {credentialRows(descriptor, instance).map((row) => (
          <div key={row.name} className="border-line bg-bg border-b px-4 py-3 last:border-b-0">
            <div className="text-mute text-xs">{row.label}</div>
            <div
              className={cn(
                'mt-[3px] truncate font-mono text-[13px]',
                row.secret && 'tracking-[.05em]',
              )}
            >
              {row.display}
            </div>
          </div>
        ))}
      </div>
      {instance.lastError !== undefined && (
        <p className="text-bad mt-2.5 mb-0 text-[12.5px] [overflow-wrap:anywhere]">
          {instance.lastError}
        </p>
      )}
    </>
  );
}

function Actions({
  instance,
  test,
  toggle,
  onEdit,
}: {
  instance: WireSourceInstance;
  test: UseMutationResult<void, Error, void>;
  toggle: UseMutationResult<void, Error, void>;
  onEdit: () => void;
}): JSX.Element {
  return (
    <div className="mt-2.5 flex flex-wrap gap-2">
      {!isDemoSource(instance) && (
        <button type="button" onClick={onEdit} className={button.small}>
          Update credentials
        </button>
      )}
      <button
        type="button"
        disabled={test.isPending}
        onClick={() => test.mutate()}
        className={cn(button.small, 'min-w-[130px]')}
      >
        {test.isPending ? 'Testing…' : 'Test connection'}
      </button>
      <button
        type="button"
        disabled={toggle.isPending}
        onClick={() => toggle.mutate()}
        className={button.small}
      >
        {instance.enabled ? 'Pause syncing' : 'Resume syncing'}
      </button>
    </div>
  );
}
